// frontend/src/components/parametres/ModuleRightsModal.jsx
// ============================================================================
// MODAL DE GESTION DES DROITS D'ACCÈS AUX MODULES + SOUS-DROITS
// ============================================================================

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { 
  X, 
  Key, 
  Save, 
  RotateCcw,
  Shield,
  CheckCircle,
  XCircle,
  LayoutDashboard,
  Users,
  Clock,
  Wallet,
  FileText,
  FolderOpen,
  Settings as SettingsIcon,
  ChevronDown,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import api from '../services/api';
import { Toast } from '../components/ui/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { useLoader } from '../contexts/LoaderContext';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

// ============================================================================
// MAPPING DES ICÔNES
// ============================================================================

const ICON_MAP = {
  LayoutDashboard: LayoutDashboard,
  Users: Users,
  Clock: Clock,
  Wallet: Wallet,
  FileText: FileText,
  FolderOpen: FolderOpen,
  Settings: SettingsIcon
};

const ModuleRightsModal = ({ user, onClose, onSuccess }) => {
  const [modules, setModules] = useState([]);
  const [rights, setRights] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const { showLoader, hideLoader } = useLoader();

  // 🔥 Feature Flags
  const { isEnabled: comptaEnabled } = useFeatureFlag('compta_module');
  const { isEnabled: baseDocEnabled } = useFeatureFlag('base_documentaire');

  // ==========================================================================
  // CHARGEMENT DES DONNÉES
  // ==========================================================================

  useEffect(() => {
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      showLoader();
      setLoading(true);
      setError(null);

      console.log('📋 Chargement modules et droits pour user:', user.id);

      const modulesResponse = await api.moduleRights.getModules();
      const rightsResponse = await api.moduleRights.getUserRights(user.id);

      if (modulesResponse.success && rightsResponse.success) {
        setModules(modulesResponse.modules);
        setRights(rightsResponse.rights);
        setIsAdmin(rightsResponse.isAdmin || false);
        
        const initialExpanded = {};
        modulesResponse.modules.forEach(module => {
          if (module.subRights && module.subRights.length > 0) {
            initialExpanded[module.key] = true;
          }
        });
        setExpandedModules(initialExpanded);
        
        console.log('✅ Données chargées');
      }
    } catch (err) {
      console.error('❌ Erreur chargement:', err);
      setError('Impossible de charger les droits d\'accès');
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // ==========================================================================
  // TOAST
  // ==========================================================================

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  const handleToggle = (moduleKey) => {
    setRights(prev => {
      const newValue = !prev[moduleKey];
      const updated = { ...prev, [moduleKey]: newValue };
      
      const module = modules.find(m => m.key === moduleKey);
      if (module && module.subRights) {
        module.subRights.forEach(sub => {
          updated[sub.key] = newValue;
        });
      }
      
      return updated;
    });
  };

  const handleSubRightToggle = (subKey) => {
    setRights(prev => ({
      ...prev,
      [subKey]: !prev[subKey]
    }));
  };

  const toggleModuleExpansion = (moduleKey) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleKey]: !prev[moduleKey]
    }));
  };

  const handleToggleAll = (value) => {
    const newRights = {};
    
    modules.forEach(module => {
      newRights[module.key] = value;
      
      if (module.subRights) {
        module.subRights.forEach(sub => {
          newRights[sub.key] = value;
        });
      }
    });
    
    setRights(newRights);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      console.log('💾 Sauvegarde des droits pour user:', user.id);

      const response = await api.moduleRights.updateUserRights(user.id, rights);

      if (response.success) {
        console.log('✅ Droits sauvegardés');
        showToast('Droits d\'accès mis à jour avec succès', 'success');
        setTimeout(() => {
          onSuccess();
        }, 1000);
      }
    } catch (err) {
      console.error('❌ Erreur sauvegarde:', err);
      setError(err.message || 'Erreur lors de la sauvegarde');
      showToast(err.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      setError(null);

      console.log('🔄 Réinitialisation des droits pour user:', user.id);

      const response = await api.moduleRights.resetUserRights(user.id);

      if (response.success) {
        console.log('✅ Droits réinitialisés');
        showToast('Droits réinitialisés avec succès', 'success');
        setShowResetConfirm(false);
        await loadData();
      }
    } catch (err) {
      console.error('❌ Erreur réinitialisation:', err);
      setError(err.message || 'Erreur lors de la réinitialisation');
      showToast(err.message || 'Erreur lors de la réinitialisation', 'error');
      setShowResetConfirm(false);
    } finally {
      setSaving(false);
    }
  };

  // ==========================================================================
  // STATISTIQUES
  // ==========================================================================

  const enabledCount = Object.keys(rights).filter(key => {
    return modules.some(m => m.key === key) && rights[key];
  }).length;
  
  const totalCount = modules.length;

  // ==========================================================================
  // RENDER PORTAL
  // ==========================================================================

  return ReactDOM.createPortal(
   <div className="fixed inset-0 flex items-center justify-center p-6" style={{ background: 'rgba(0,0,0,0.6)', zIndex: 9998 }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl h-[85vh] overflow-hidden flex flex-col hp-scale-in">
        
        {/* Header */}
        <div 
          className="text-white px-8 py-6 flex items-center justify-between flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #448696 0%, #6fa3b0 100%)' }}
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
              <Key size={28} />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Gestion des droits d'accès</h2>
              <p className="text-sm text-white/90 mt-1 font-medium">
                {user.name} • {user.email}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X size={28} />
          </button>
        </div>



        {/* Barre stats FIXE */}
        {!loading && !isAdmin && (
          <div className="flex-shrink-0 px-8 py-4 bg-white border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-gray-600 mb-1">Modules accessibles</p>
                <p className="text-3xl font-bold text-gray-900">{enabledCount} / {totalCount}</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handleToggleAll(true)}
                  className="px-5 py-2.5 text-white text-sm rounded-xl font-semibold transition-all hover:shadow-lg disabled:opacity-50"
                  style={{ backgroundColor: '#448696' }}
                  disabled={saving}
                >
                  Tout activer
                </button>
                <button
                  onClick={() => handleToggleAll(false)}
                  className="px-5 py-2.5 bg-red-600 text-white text-sm rounded-xl hover:bg-red-700 transition-all hover:shadow-lg font-semibold disabled:opacity-50"
                  disabled={saving}
                >
                  Tout désactiver
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Content scrollable */}
        <div className="flex-1 overflow-y-auto modal-scroll p-8" style={{ backgroundColor: '#fdfcf5' }}>
        
          {/* Admin notice */}
          {isAdmin && (
            <div 
              className="rounded-xl p-5 mb-6 flex items-start gap-4 border-2"
              style={{ backgroundColor: '#fed77420', borderColor: '#fed774' }}
            >
              <Shield style={{ color: '#d4a843' }} className="flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-base font-bold text-gray-900">
                  Administrateur
                </p>
                <p className="text-sm text-gray-700 mt-1 font-medium">
                  Cet utilisateur est administrateur et a automatiquement accès à tous les modules. Les droits ne peuvent pas être modifiés.
                </p>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && null}

          {/* Error */}
          {error && !loading && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5 mb-6 flex items-start gap-4">
              <AlertCircle className="text-red-600 flex-shrink-0" size={24} />
              <p className="text-base font-semibold text-red-800">{error}</p>
            </div>
          )}

          {/* Stats */}
        
          {/* Liste des modules - GRID 2 colonnes */}
         {!loading && !isAdmin && (() => {
            // 🔥 Filtrer les modules selon les feature flags
            const filteredModules = modules.filter(module => {
              if (module.key === 'payroll' && !comptaEnabled) return false;
              if (module.key === 'documents' && !baseDocEnabled) return false;
              return true;
            });

            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {filteredModules.map((module, index) => {
                  const Icon = ICON_MAP[module.icon] || LayoutDashboard;
                  const enabled = rights[module.key];
                  const hasSubRights = module.subRights && module.subRights.length > 0;
                  const isExpanded = expandedModules[module.key];

                  return (
                    <div 
                      key={module.key}
                      className="hp-fade-in-up"
                      style={{ animationDelay: `${index * 30}ms` }}
                    >
                      {/* Module principal */}
                      <div
                        className={`border-2 rounded-xl p-5 transition-all shadow-sm ${
                          enabled
                            ? 'border-transparent'
                            : 'border-gray-100 bg-white'
                        }`}
                        style={enabled ? { backgroundColor: '#44869620' } : {}}
                      >
                        <div className="flex items-center justify-between">
                          {/* Info module */}
                          
                          <div className="flex items-center gap-3 flex-1">
                            {/* Bouton expand si sous-droits */}
                            {hasSubRights && (
                              <button
                                onClick={() => toggleModuleExpansion(module.key)}
                                className="p-1.5 hover:bg-white/50 rounded transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronDown size={20} className="text-gray-600" />
                                ) : (
                                  <ChevronRight size={20} className="text-gray-600" />
                                )}
                              </button>
                            )}
                            
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center"
                              style={{ backgroundColor: enabled ? '#448696' : '#e5e7eb' }}
                            >
                              <Icon size={24} className={enabled ? 'text-white' : 'text-gray-400'} />
                            </div>
                            
                            <div>
                              <h3 className="font-bold text-gray-900 text-base">
                                {module.label}
                              </h3>
                              <p className="text-sm text-gray-600 font-medium">
                               
                              </p>
                              {hasSubRights && (
                                <p 
                                  className="text-xs font-semibold mt-1"
                                  style={{ color: '#d4a843' }}
                                >
                                  {module.subRights.length} vues disponibles
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Toggle principal */}
                          <button
                            onClick={() => handleToggle(module.key)}
                            disabled={saving}
                            className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50"
                            style={{
                              backgroundColor: enabled ? '#448696' : '#d1d5db'
                            }}
                          >
                            <span
                              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                enabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>

                      {/* Sous-droits (si expanded) */}
                      {hasSubRights && isExpanded && (
                        <div className="ml-16 mt-3 space-y-2">
                          {module.subRights.map((subRight) => {
                            const subEnabled = rights[subRight.key];
                            
                            return (
                              <div
                                key={subRight.key}
                                className={`border-l-4 pl-4 py-3 rounded-r-xl transition-all ${
                                  subEnabled
                                    ? 'bg-white'
                                    : 'bg-gray-50'
                                }`}
                                style={{
                                  borderColor: subEnabled ? '#fed774' : '#d1d5db'
                                }}
                              >
                             <div className="flex items-center justify-between pr-5">
                              <div>
                                <h4 className="text-sm font-bold text-gray-900">
                                  {subRight.label}
                                </h4>
                                <p className="text-xs text-gray-600 mt-0.5 font-medium">
                                  {subRight.description}
                                </p>
                              </div>

                                  {/* Toggle sous-droit */}
                                <button
                                onClick={() => handleSubRightToggle(subRight.key)}
                                disabled={saving || !enabled}
                                className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 flex-shrink-0"
                                style={{
                                  backgroundColor: subEnabled ? '#fed774' : '#d1d5db'
                                }}
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                    subEnabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>


                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        {!loading && !isAdmin && (
          <div className="border-t border-gray-100 px-8 py-5 bg-white flex-shrink-0">
            <div className="flex items-center justify-between">
              {/* Bouton réinitialiser */}
              <button
                onClick={() => setShowResetConfirm(true)}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-semibold text-base disabled:opacity-50"
              >
                <RotateCcw size={20} />
                Réinitialiser
              </button>

              {/* Actions principales */}
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  disabled={saving}
                  className="px-5 py-3 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-all font-semibold text-base disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-8 py-3 text-white rounded-xl transition-all hover:shadow-lg font-semibold text-base disabled:opacity-50"
                  style={{ backgroundColor: '#448696' }}
                >
                  {saving ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Save size={20} />
                      Sauvegarder
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Dialog pour réinitialisation */}
     <ConfirmDialog
      isOpen={showResetConfirm}
      onClose={() => setShowResetConfirm(false)}
      onConfirm={handleReset}
      title="Réinitialiser les droits ?"
      description="L'utilisateur aura accès à tous les modules. Cette action peut être annulée en modifiant à nouveau les droits."
      confirmText="Réinitialiser"
      cancelText="Annuler"
      type="warning"
    />

      {/* Toast */}
      {toast.show && (
        <Toast
          show={toast.show}
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ show: false, message: '', type: '' })}
        />
      )}

    </div>,
    document.body
  );
};

export default ModuleRightsModal;